import { FeatureFlagsRulesServiceBase, HttpService, LocalContext, ObservableCollection, QueryBus, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { AddressModel, DocumentPaymentListItemModel, ExchangeRateModel, RelatedEntity, SaleDocumentItemModel, SaleGetDocumentDTO, SaleProFormaModel, SaleUpdateDocumentDTO, VatTableItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, documentDeliveryAddressFilled, serializeAddressModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentItemModel, serializeDocumentPaymentListItemModel } from '../common/serialziation-utils';
import { ProductJpkVatGroupCollectionQuery } from '../product/queries/product-jpk-vat-group.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { SaleJpkVatGroupCollectionQuery } from './queries/sale-jpk-vat-group.query-base';
import { SaleProFormaBO } from './sale-pro-forma.bo';

@Repository({
    boType: SaleProFormaBO,
    apiController: TaggedUrl.join('sales', [Tags.Api]),
    dto: {
        get: SaleGetDocumentDTO,
        update: SaleUpdateDocumentDTO
    }
})
export class SaleProFormaRepositoryBase extends RepositoryBase<SaleProFormaBO, SaleProFormaModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO> {

    public constructor(
        http: HttpService,
        private readonly weaver: Weaver,
        private readonly productRepository: ProductRepositoryContract,
        private readonly clientRepository: ClientRepositoryBase,
        private readonly queryBus: QueryBus,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(http);
    }

    protected createBO(): SaleProFormaBO {
        return new SaleProFormaBO(this.weaver, this.productRepository, this.featureFlagsRulesService, this.clientRepository, this.queryBus);
    }

    public async fromPlainObjectAsync(item: SaleGetDocumentDTO): Promise<SaleProFormaModel> {
        const document = new SaleProFormaModel(pickBy<Partial<SaleProFormaModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,

            relatedInventoryDocuments: item.relatedInventoryDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            includesInventoryControl: item.includesInventoryControl,
            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            communityTransactionId: item.communityTransactionId,
            currencyId: item.currencyId,
            documentNumber: item.documentNumber,
            dueDate: getStartOfDate(item.dueDate),
            dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
            favourite: item.favourite,
            grossValue: item.totalGrossValue,
            issueDate: getStartOfDate(item.issueDate),
            issuedByVatPayer: item.issuedByVatPayer,
            issuer: item.issuer,
            kind: item.kind,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            netValue: item.totalNetValue,
            note: item.note,
            numberingId: item.numberingId,
            orderDate: item.orderDate ? getStartOfDate(item.orderDate) : null,
            paymentMethodId: item.paymentMethodId,
            splitPayment: item.splitPayment,
            recipient: item.recipient,
            transactionLocation: item.transactionLocation,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            customDueDateDescription: item.customDueDateDescription,
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = (item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            name: item.deliveryAddress.name,
            zipCode: item.deliveryAddress.zipCode,
            line1: item.deliveryAddress.address,
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId
        }, val => val !== void 0)) : null) as AddressModel;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleDocumentItemModel(pickBy<Partial<SaleDocumentItemModel>>({
                cnCode: documentItem.cnCode,
                cost: documentItem.cost,
                relatedInventoryItemsIds: documentItem.relatedInventoryItemsIds || [],
                includesInventoryControl: documentItem.includesInventoryControl,
                ordinalNumber: documentItem.no,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                jpkVatGroups: documentItem.jpkVatGroups,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                note: documentItem.note,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                price_netBeforeDiscount: documentItem.unitNetPrice,
                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                quantity: documentItem.quantity,
                unitCost: documentItem.unitCost,
                id: documentItem.id,
                unitId: documentItem.unitId,
                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,
                vatRateId: documentItem.vatRateId
            }, val => val !== void 0));
        }));

        if (item.exchangeRate) {
            document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0));
        }

        document.payments = new ObservableCollection(item.payments.map(item => {
            return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                id: item.id,

                date: item.date,
                value: item.amount,
                onDocument: item.onDocument
            }, val => val !== void 0));
        }));

        document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.currencyGross ?? item.gross,
                netValue: item.currencyNet ?? item.net,
                vatRateId: item.vatRateId,
                vatValue: item.currencyVat ?? item.vat,
            }, val => val !== void 0));
        }));

        document.vatTableItemsInBaseCurrency = new ObservableCollection(item.vatTable.items.map(item => {
            return item.currencyGross ? new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.gross,
                netValue: item.net,
                vatRateId: item.vatRateId,
                vatValue: item.vat
            }, val => val !== void 0)) : null;
        }).filter(item => item !== null));

        return document;
    }

    protected async preparePlainObjectAsync(model: SaleProFormaModel, ctx: LocalContext): Promise<SaleUpdateDocumentDTO> {
        const jpkVatDocumentGroupCodes = await ctx.getAsync(SaleJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));
        const jpkVatProductGroupCodes = await ctx.getAsync(ProductJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));

        const item: Omit<SaleUpdateDocumentDTO, 'ksefSubmissionMode'> = {
            id: model.id,
            duplicatePreventionId: model.duplicatePreventionId,
            version: model.version,

            bankAccountId: model.bankAccountNumberId!,
            relatedInventoryDocuments: model.relatedInventoryDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            baseCurrencyBankAccountId: model.baseCurrencyBankAccountNumberId as number,
            buyer: model.buyer ? serializeDocumentBuyerModel(model.buyer) : null as any,
            calculationMethod: model.calculationMethod,
            cashBasis: model.cashBasis,
            client: model.client ? serializeDocumentClientModel(model.client) : null as any,
            communityTransactionId: model.communityTransactionId,
            currencyId: model.currencyId,
            deliveryAddress: documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress) : null as any,
            documentNumber: model.documentNumber,
            dueDate: getStartOfDate(model.dueDate),
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null as any,
            favourite: model.favourite,
            issueDate: getStartOfDate(model.issueDate),
            goodsIssueDate: null,
            issuedByVatPayer: model.issuedByVatPayer,
            issuer: model.issuer,
            items: model.items.map(serializeDocumentItemModel),
            jpkVatDocumentGroups: model.jpkVatGroups.filter(g => jpkVatDocumentGroupCodes.includes(g)),
            jpkVatProductGroups: model.jpkVatGroups.filter(g => jpkVatProductGroupCodes.includes(g)),
            kind: model.kind,
            note: model.note,
            numberingId: model.numberingId,
            orderDate: model.orderDate ? getStartOfDate(model.orderDate) : null,
            paymentMethodId: model.paymentMethodId,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),
            recipient: model.recipient,
            splitPayment: model.splitPayment,
            transactionLocation: model.transactionLocation,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            customDueDateDescription: model.customDueDateDescription,
            vatReleaseReasonId: model.vatReleaseReasonId,
            vatReleaseReasonCustomName: model.vatReleaseReasonCustomName!,
            vatReleaseReasonSource: model.vatReleaseReasonSource!,
            rateNP: null,
            reverseCharge: null,
            adjustPaymentDate: false
        };

        if (model.id && model.documentNumber) {
            item.documentNumber = model.documentNumber;
        }
        return item as SaleUpdateDocumentDTO;
    }
}
